import React, { useEffect, useMemo, useState } from "react";
import Nav from "components/layout/Nav";
import { MenuBar } from "components/layout/MenuBar";
import {
    createCategory,
    deleteCategory,
    fetchCategories,
    updateCategory,
} from "api/categories";
import { showToast } from "components/shared/Toast";
import { FiPlus, FiTrash2 } from "react-icons/fi";

const segmentOptions = [
    { value: "trek", label: "Trek" },
    { value: "travel", label: "Travel" },
    { value: "tour", label: "Tour" },
    { value: "upcoming-events", label: "Upcoming Events" },
    { value: "archeological-sites", label: "Archeological Sites" },
    { value: "wildlife-sanctuary", label: "Wildlife Sanctuary" },
    { value: "winery", label: "Winery" },
    { value: "camping", label: "Camping" },
];

const defaultFormState = {
    segment: "trek",
    parentId: "",
    name: "",
    iconKey: "map",
    isFeaturedChoice: false,
    sortOrder: 0,
    iconFile: null,
};

const flattenCategoryTree = (items = []) =>
    items.flatMap((item) => [
        {
            ...item,
            parentName: "",
        },
        ...(item.children || []).map((child) => ({
            ...child,
            parentName: item.name,
        })),
    ]);

const CategoriesCMS = () => {
    const [categories, setCategories] = useState([]);
    const [formData, setFormData] = useState(defaultFormState);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [iconPreview, setIconPreview] = useState("");
    const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);

    const resetCreateForm = () => {
        if (iconPreview) {
            URL.revokeObjectURL(iconPreview);
        }

        setFormData(defaultFormState);
        setIconPreview("");
    };

    const loadCategories = async () => {
        try {
            setIsLoading(true);
            const response = await fetchCategories();
            setCategories(Array.isArray(response) ? response : []);
        } catch (error) {
            console.error("Failed to load categories", error);
            showToast("Failed to load categories.", "error");
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadCategories();
    }, []);

    useEffect(() => {
        return () => {
            if (iconPreview) {
                URL.revokeObjectURL(iconPreview);
            }
        };
    }, [iconPreview]);

    const parentCategoryOptions = useMemo(() => {
        return categories
            .filter((item) => item.segment === formData.segment)
            .flatMap((item) => {
                const parent = {
                    value: item._id,
                    label: item.name,
                };

                return [parent];
            });
    }, [categories, formData.segment]);

    const groupedCategories = useMemo(() => {
        return segmentOptions.map((segment) => ({
            ...segment,
            items: flattenCategoryTree(
                categories.filter((item) => item.segment === segment.value)
            ),
        }));
    }, [categories]);
    const totalCategories = useMemo(
        () => groupedCategories.reduce((total, group) => total + group.items.length, 0),
        [groupedCategories]
    );
    const featuredCategories = useMemo(
        () => groupedCategories.reduce(
            (total, group) => total + group.items.filter((item) => item.isFeaturedChoice).length,
            0
        ),
        [groupedCategories]
    );

    const handleChange = (event) => {
        const { name, value, type, checked, files } = event.target;

        if (name === "iconFile") {
            const file = files?.[0] || null;
            if (iconPreview) {
                URL.revokeObjectURL(iconPreview);
            }
            setFormData((previous) => ({
                ...previous,
                iconFile: file,
            }));
            setIconPreview(file ? URL.createObjectURL(file) : "");
            return;
        }

        setFormData((previous) => ({
            ...previous,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    const handleSubmit = async (event) => {
        event.preventDefault();
        if (!formData.name.trim()) {
            showToast("Category name is required.", "error");
            return;
        }

        try {
            setIsSaving(true);
            const payload = new FormData();
            payload.append("segment", formData.segment);
            payload.append("parentId", formData.parentId || "");
            payload.append("name", formData.name);
            payload.append("sortOrder", String(Number(formData.sortOrder) || 0));
            payload.append("isFeaturedChoice", String(Boolean(formData.isFeaturedChoice)));
            payload.append("isActive", "true");

            if (formData.iconFile) {
                payload.append("icon", formData.iconFile);
            }

            await createCategory(payload);
            showToast("Category created successfully.", "success");
            resetCreateForm();
            setIsCreateModalOpen(false);
            await loadCategories();
        } catch (error) {
            console.error("Failed to create category", error);
            showToast(
                error?.response?.data?.message || "Failed to create category.",
                "error"
            );
        } finally {
            setIsSaving(false);
        }
    };

    const openCreateModal = () => {
        resetCreateForm();
        setIsCreateModalOpen(true);
    };

    const closeCreateModal = () => {
        resetCreateForm();
        setIsCreateModalOpen(false);
    };

    const handleFeaturedToggle = async (category) => {
        try {
            const payload = new FormData();
            payload.append("isFeaturedChoice", String(!category.isFeaturedChoice));
            await updateCategory(category._id, payload);
            showToast("Category updated.", "success");
            await loadCategories();
        } catch (error) {
            console.error("Failed to update category", error);
            showToast("Failed to update category.", "error");
        }
    };

    const handleDelete = async (id) => {
        try {
            await deleteCategory(id);
            showToast("Category deleted.", "success");
            await loadCategories();
        } catch (error) {
            console.error("Failed to delete category", error);
            showToast("Failed to delete category.", "error");
        }
    };

    return (
        <>
            <Nav />
            <div className="row">
                <MenuBar />
                <main className="adminPageMain adminPageMain--slate">
                    <div className="adminPageShell categoriesPanel">
                        <section className="categoriesHero">
                            <div className="categoriesHero__top">
                                <div>
                                    <p className="categoriesHero__eyebrow">Category Management</p>
                                    <h2 className="categoriesHero__heading">Manage categories</h2>
                                </div>
                                <button
                                    type="button"
                                    className="categoriesOpenButton"
                                    onClick={openCreateModal}
                                >
                                    <FiPlus />
                                    Create Category
                                </button>
                            </div>

                            <div className="categoriesStats">
                                <article className="categoriesStatCard">
                                    <span className="categoriesStatCard__label">Total Categories</span>
                                    <span className="categoriesStatCard__value">{totalCategories}</span>
                                </article>
                                <article className="categoriesStatCard">
                                    <span className="categoriesStatCard__label">Choice Categories</span>
                                    <span className="categoriesStatCard__value">{featuredCategories}</span>
                                </article>
                                <article className="categoriesStatCard">
                                    <span className="categoriesStatCard__label">Segments Covered</span>
                                    <span className="categoriesStatCard__value">{groupedCategories.filter((group) => group.items.length).length}</span>
                                </article>
                            </div>
                        </section>

                        <div className="categoriesGrid">
                            <div className="categoriesList">
                                {isLoading ? (
                                    <section className="categoriesSegmentCard">
                                        <h3 className="categoriesSegmentCard__title">Loading categories...</h3>
                                        <p className="categoriesSegmentCard__description">
                                            Fetching your current taxonomy structure.
                                        </p>
                                    </section>
                                ) : (
                                    groupedCategories.map((group) => (
                                        <section className="categoriesSegmentCard" key={group.value}>
                                            <div className="categoriesSegmentCard__header">
                                                <div>
                                                    <h3 className="categoriesSegmentCard__title">{group.label}</h3>
                                                    <p className="categoriesSegmentCard__description">
                                                        Managed categories available for the {group.label.toLowerCase()} segment.
                                                    </p>
                                                </div>
                                                <span className="categoriesSegmentCard__count">
                                                    {group.items.length}
                                                </span>
                                            </div>

                                            {group.items.length ? (
                                                <div className="categoriesCards">
                                                    {group.items.map((category) => {
                                                        return (
                                                            <article className="categoriesCard" key={category._id}>
                                                                <div className="categoriesCard__top">
                                                                    <div className="d-flex gap-3">
                                                                        <div className="categoriesCard__icon">
                                                                            {category.icon?.filePath ? (
                                                                                <img
                                                                                    src={category.icon.filePath}
                                                                                    alt={category.name}
                                                                                    style={{ width: "28px", height: "28px", objectFit: "contain" }}
                                                                                />
                                                                            ) : (
                                                                                <span style={{ fontSize: "12px", fontWeight: 700 }}>
                                                                                    {category.name.slice(0, 2).toUpperCase()}
                                                                                </span>
                                                                            )}
                                                                        </div>
                                                                        <div>
                                                                            <h4 className="categoriesCard__name">
                                                                                {category.name}
                                                                            </h4>
                                                                            <div className="categoriesCard__meta">
                                                                                {category.parentName
                                                                                    ? `Sub category of ${category.parentName}`
                                                                                    : "Parent category"}
                                                                            </div>
                                                                        </div>
                                                                    </div>

                                                                    <button
                                                                        type="button"
                                                                        className="btn btn-sm btn-outline-danger"
                                                                        onClick={() => handleDelete(category._id)}
                                                                        aria-label={`Delete ${category.name}`}
                                                                    >
                                                                        <FiTrash2 />
                                                                    </button>
                                                                </div>

                                                                <div className="categoriesCard__actions">
                                                                    <span className="categoriesCard__pill">
                                                                        {category.isFeaturedChoice ? "Choice enabled" : "Standard"}
                                                                    </span>
                                                                    <button
                                                                        type="button"
                                                                        className={`btn btn-sm ${category.isFeaturedChoice ? "btn-dark" : "btn-outline-dark"}`}
                                                                        onClick={() => handleFeaturedToggle(category)}
                                                                    >
                                                                        {category.isFeaturedChoice ? "Disable Choice" : "Enable Choice"}
                                                                    </button>
                                                                </div>
                                                            </article>
                                                        );
                                                    })}
                                                </div>
                                            ) : (
                                                <div className="categoriesEmptyState">
                                                    No categories added for this segment yet.
                                                </div>
                                            )}
                                        </section>
                                    ))
                                )}
                            </div>
                        </div>

                        {isCreateModalOpen ? (
                            <div className="modal-overlay" onClick={closeCreateModal}>
                                <div
                                    className="modal-content categoriesModal"
                                    onClick={(event) => event.stopPropagation()}
                                >
                                    <button
                                        type="button"
                                        className="close-button"
                                        onClick={closeCreateModal}
                                        aria-label="Close category popup"
                                    >
                                        &times;
                                    </button>

                                    <h3 className="categoriesCreateCard__title">Create Category</h3>
                                    <p className="categoriesCreateCard__description">
                                        Add a parent category or create a sub category under the same segment.
                                    </p>

                                    <form onSubmit={handleSubmit} className="categoriesForm">
                                        <div className="categoriesField">
                                            <label htmlFor="category-segment">Segment</label>
                                            <select
                                                id="category-segment"
                                                name="segment"
                                                value={formData.segment}
                                                onChange={handleChange}
                                            >
                                                {segmentOptions.map((segment) => (
                                                    <option key={segment.value} value={segment.value}>
                                                        {segment.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="categoriesField">
                                            <label htmlFor="category-parent">Parent Category</label>
                                            <select
                                                id="category-parent"
                                                name="parentId"
                                                value={formData.parentId}
                                                onChange={handleChange}
                                            >
                                                <option value="">Create as parent category</option>
                                                {parentCategoryOptions.map((item) => (
                                                    <option key={item.value} value={item.value}>
                                                        {item.label}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="categoriesField">
                                            <label htmlFor="category-name">Category Name</label>
                                            <input
                                                id="category-name"
                                                name="name"
                                                value={formData.name}
                                                onChange={handleChange}
                                                placeholder="Enter category name"
                                            />
                                        </div>

                                        <div className="categoriesField">
                                            <label htmlFor="category-icon">Icon</label>
                                            <input
                                                id="category-icon"
                                                type="file"
                                                name="iconFile"
                                                accept=".png,.jpg,.jpeg,.svg,image/png,image/jpeg,image/svg+xml"
                                                onChange={handleChange}
                                            />
                                            {iconPreview ? (
                                                <img
                                                    src={iconPreview}
                                                    alt="Category icon preview"
                                                    style={{ width: "56px", height: "56px", objectFit: "contain" }}
                                                />
                                            ) : null}
                                        </div>

                                        <div className="categoriesField">
                                            <label htmlFor="category-sort-order">Sort Order</label>
                                            <input
                                                id="category-sort-order"
                                                type="number"
                                                name="sortOrder"
                                                value={formData.sortOrder}
                                                onChange={handleChange}
                                            />
                                        </div>

                                        <div className="categoriesToggleRow">
                                            <div className="categoriesToggleRow__copy">
                                                <strong>Enable Choice Category</strong>
                                                <span>Use this category inside the hero slider choice toggle.</span>
                                            </div>
                                            <div className="form-check form-switch m-0">
                                                <input
                                                    className="form-check-input"
                                                    type="checkbox"
                                                    name="isFeaturedChoice"
                                                    id="isFeaturedChoice"
                                                    checked={formData.isFeaturedChoice}
                                                    onChange={handleChange}
                                                />
                                            </div>
                                        </div>

                                        <button
                                            type="submit"
                                            className="categoriesSubmit"
                                            disabled={isSaving}
                                        >
                                            {isSaving ? "Saving..." : "Create Category"}
                                        </button>
                                    </form>
                                </div>
                            </div>
                        ) : null}
                    </div>
                </main>
            </div>
        </>
    );
};

export default CategoriesCMS;
